import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppearance } from '../../app/appearance';
import {
  hasStoredSiteAppearance,
  readSiteAppearance,
  SITE_APPEARANCE_UPDATED_EVENT,
} from '../../app/site-appearance/site-appearance-contract';
import { applySiteAppearanceScope } from '../../app/site-appearance/site-appearance-scope';

import '../../app/site-appearance/site-appearance-fonts.css';

/**
 * The recovery screen is intentionally chrome-free. Reuse the same sanitized
 * Appearance resolver / FlexUI token bridge as authenticated UI, without
 * mounting Marketing or Management navigation and without touching root portals.
 */
export function ErrorAppearance({ children }: { children: ReactNode }) {
  // Sync legacy light/dark when no custom Appearance has been saved.
  useAppearance();
  const { i18n } = useTranslation();
  const root = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;

    const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    let restore: (() => void) | undefined;
    const sync = () => {
      restore?.();
      // Editor visibility is not theme visibility. A committed Appearance
      // must apply on *every* route, including direct 404 loads, even if the
      // Studio launcher is disabled. Without a committed preset, inherit the
      // native Slider tokens instead of forcing the default violet preset.
      restore = hasStoredSiteAppearance()
        ? applySiteAppearanceScope(element, readSiteAppearance(), media?.matches ?? false, 'manage')
        : undefined;
    };

    sync();
    media?.addEventListener('change', sync);
    globalThis.addEventListener('storage', sync);
    globalThis.addEventListener(SITE_APPEARANCE_UPDATED_EVENT, sync);
    return () => {
      media?.removeEventListener('change', sync);
      globalThis.removeEventListener('storage', sync);
      globalThis.removeEventListener(SITE_APPEARANCE_UPDATED_EVENT, sync);
      restore?.();
    };
  }, []);

  return (
    <main className="slider-error-appearance" data-error-appearance-scope
      dir={i18n.dir()} ref={root}>
      {children}
    </main>
  );
}
